import React from 'react';
import { useTheme } from '../ThemeContext';
import Footer from './Footer';
import { POLICY_EFFECTIVE_DATE, POLICY_LAST_UPDATED, followPolicyLink } from '../utils/legal';
import { parseLegalMarkdown, publishableLegalMarkdown, standaloneTrialTerms } from '../utils/legalMarkdown';
import docs from '../legalDocs/bundle.json';

function Inline({ nodes }) {
  return (nodes || []).map((node, index) => {
    if (typeof node === 'string') return <React.Fragment key={index}>{node}</React.Fragment>;
    if (node.type === 'bold') return <strong key={index}><Inline nodes={node.children} /></strong>;
    if (node.type === 'italic') return <em key={index}><Inline nodes={node.children} /></em>;
    if (node.type === 'code') return <code key={index}>{node.text}</code>;
    if (node.type === 'link') {
      const href = node.href || '';
      const internal = href.startsWith('/');
      return (
        <a
          key={index}
          href={href}
          onClick={internal ? followPolicyLink : undefined}
          {...(internal ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
        >
          <Inline nodes={node.children} />
        </a>
      );
    }
    return null;
  });
}

function Block({ block, colors }) {
  const anchor = block.id ? { id: block.id } : {};
  if (block.type === 'h1') return <h1 {...anchor} style={{ ...styles.title, color: colors.text }}><Inline nodes={block.children} /></h1>;
  if (block.type === 'h2') return <h2 {...anchor} style={{ ...styles.heading, color: colors.text }}><Inline nodes={block.children} /></h2>;
  if (block.type === 'h3') return <h3 {...anchor} style={{ ...styles.subheading, color: colors.text }}><Inline nodes={block.children} /></h3>;
  if (block.type === 'p') return <p style={{ ...styles.text, color: colors.text }}><Inline nodes={block.children} /></p>;
  if (block.type === 'quote') {
    return <p style={{ ...styles.text, color: colors.textSecondary }}><Inline nodes={block.children} /></p>;
  }
  if (block.type === 'hr') return <hr style={{ border: 0, borderTop: `1px solid ${colors.border}`, margin: '1.5rem 0' }} />;
  if (block.type === 'ul' || block.type === 'ol') {
    const Tag = block.type === 'ol' ? 'ol' : 'ul';
    return (
      <Tag style={{ ...styles.list, color: colors.text }}>
        {block.items.map((item, index) => (
          <li key={index}><Inline nodes={item} /></li>
        ))}
      </Tag>
    );
  }
  if (block.type === 'table') {
    return (
      <div style={styles.tableWrap}>
        <table style={{ ...styles.table, color: colors.text }}>
          <thead>
            <tr>
              {block.headers.map((cell, index) => (
                <th key={index} style={{ ...styles.cell, borderColor: colors.border }}><Inline nodes={cell} /></th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, index) => (
                  <td key={index} style={{ ...styles.cell, borderColor: colors.border }}><Inline nodes={cell} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
}

const LegalDocument = ({ doc, onBack, children }) => {
  const { colors } = useTheme();
  const source = doc === 'trial' ? standaloneTrialTerms(docs[doc]) : docs[doc];
  const blocks = parseLegalMarkdown(publishableLegalMarkdown(source, POLICY_EFFECTIVE_DATE, POLICY_LAST_UPDATED));

  return (
    <div className="app-scroll" style={{ ...styles.container, background: colors.background, color: colors.text }}>
      <div style={styles.content}>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            style={{ ...styles.backButton, color: '#59c4c4', borderColor: '#59c4c4' }}
          >
            ← Back
          </button>
        )}
        {blocks.map((block, index) => (
          <Block key={index} block={block} colors={colors} />
        ))}
        {children}
      </div>
      <Footer />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '100%',
    overflowX: 'hidden',
    paddingTop: 'calc(2rem + env(safe-area-inset-top, 0px))',
    paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))',
    paddingLeft: 'max(1rem, env(safe-area-inset-left, 0px))',
    paddingRight: 'max(1rem, env(safe-area-inset-right, 0px))',
  },
  content: { maxWidth: '800px', margin: '0 auto', width: '100%', minWidth: 0, overflowWrap: 'break-word', flex: '1 0 auto' },
  backButton: {
    background: 'transparent',
    border: '2px solid',
    padding: '0.5rem 1rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontWeight: 'bold',
    marginBottom: '2rem',
  },
  title: { fontSize: '2.5rem', marginBottom: '0.5rem' },
  heading: { fontSize: '1.5rem', marginBottom: '1rem', marginTop: '1.5rem' },
  subheading: { fontSize: '1.2rem', marginBottom: '0.75rem', marginTop: '1rem' },
  text: { lineHeight: '1.8', marginBottom: '1rem' },
  list: { lineHeight: '1.8', marginLeft: '2rem', marginBottom: '1rem' },
  tableWrap: { overflowX: 'auto', marginBottom: '1rem' },
  table: { borderCollapse: 'collapse', width: '100%', fontSize: '0.95rem' },
  cell: { border: '1px solid', padding: '0.5rem', textAlign: 'left', verticalAlign: 'top' },
};

export default LegalDocument;
